import React, { FC, useEffect, useState } from 'react';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { SCUITextField, SCUIButton, SCUIDateRanger, SCUILoadingLinear } from '../../atomic/atoms';
import { useTranslation } from 'react-i18next';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { Grid, Tooltip, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Swal from 'sweetalert2';
import { AuditLogsQuery } from '@/services/query';
import TimeHelper from '../../utils/time';

export const AuditLogs: FC<any> = () => {
    const [refresh, setRefresh] = React.useState(false);
    const [totalCount, setTotalCount] = React.useState(0);
    const [auditLogs, setAudiLogs] = React.useState<any>([]);
    const { t } = useTranslation();
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [searchPath, setSearchPath] = useState<string>('');
    const [searchUserName, setSearchUserName] = useState<string>('');
    const [searchResponseTime, setSearchResponseTime] = useState<any>();
    const [startDate, setStartDate] = React.useState<any>(null);
    const [endDate, setEndDate] = React.useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const _handlChangeDate = (event: any) => {
        const start = event[0];
        const end = event[1];
        if (start && end) {
            const startDate = new Date(start);
            const endDate = new Date(end);

            const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays > 10) {
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('auditLogs.messages.dateRangeError'),
                });
                return;
            }
        }

        setStartDate(start);
        setEndDate(end);
    };
    const columns: ExtendedGridColDef[] = [
        {
            field: 'id',
            headerName: t('auditLogs.columns.id'),
            flex: 0.5,
            minWidth: 80,
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'requestTime',
            headerName: t('auditLogs.columns.requestTime'),
            flex: 1.2,
            minWidth: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                if (!params.value) return '';
                return TimeHelper.formatDateHourSecond(params.value);
            },
        },
        {
            field: 'method',
            headerName: t('auditLogs.columns.method'),
            flex: 0.6,
            minWidth: 80,
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'path',
            headerName: t('auditLogs.columns.path'),
            flex: 1.5,
            minWidth: 200,
            headerAlign: 'center',
            align: 'left',
            renderCell: (params: any) => {
                return (
                    <Box sx={{ width: '100%', maxHeight: '100%' }}>
                        <Tooltip title={<span>{params.row.path}</span>}>
                            <p
                                style={{
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    maxWidth: '100%',
                                    margin: 0,
                                    padding: '0px 8px',
                                }}
                            >
                                {params.row.path}
                            </p>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: 'queryString',
            headerName: t('auditLogs.columns.queryString'),
            flex: 1.2,
            minWidth: 150,
            headerAlign: 'center',
            align: 'left',
            renderCell: (params: any) => {
                return (
                    <div style={{ width: '100%' }}>
                        <Tooltip title={<span>{params.row.queryString || t('auditLogs.messages.noQueryString')}</span>}>
                            <p
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                    margin: 0,
                                    padding: '0px 8px',
                                }}
                            >
                                {params.row.queryString || '-'}
                            </p>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            field: 'statusCode',
            headerName: t('auditLogs.columns.statusCode'),
            flex: 0.8,
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            renderCell: (params: any) => {
                const statusCode = params.value;
                const isSuccess = statusCode >= 200 && statusCode <= 299;
                const statusColor = isSuccess ? '#4caf50' : '#f44336'; // Green for success, red for error

                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '24px',
                                backgroundColor: statusColor,
                                color: 'white',
                                padding: '0 8px',
                                borderRadius: '4px',
                                fontWeight: 'bold',
                                fontSize: '12px',
                            }}
                        >
                            {statusCode}
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: 'errorMessage',
            headerName: t('auditLogs.columns.errorMessage'),
            flex: 1.2,
            minWidth: 150,
            headerAlign: 'center',
            align: 'left',
            renderCell: (params: any) => {
                return (
                    <div style={{ width: '100%' }}>
                        <Tooltip title={<span>{params.row.errorMessage || t('auditLogs.messages.noError')}</span>}>
                            <p
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                    margin: 0,
                                    padding: '0px 8px',
                                }}
                            >
                                {params.row.errorMessage || '-'}
                            </p>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            field: 'userName',
            headerName: t('auditLogs.columns.userName'),
            flex: 1,
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'ipAddress',
            headerName: t('auditLogs.columns.ipAddress'),
            flex: 1,
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'responseTimeMs',
            headerName: t('auditLogs.columns.responseTime'),
            flex: 0.8,
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: { value?: any }) => {
                if (!params.value) return '';
                return `${params.value} ms`;
            },
        },
        {
            field: 'countryId',
            headerName: t('auditLogs.columns.countryId'),
            flex: 0.6,
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
        },
    ];

    const GetAuditLogs = async () => {
        try {
            setIsLoading(true);
            const requestParams = {
                page: pageNumber,
                pageSize: pageSize,
                userName: searchUserName || undefined,
                url: searchPath || undefined,
                responseTimeMS: searchResponseTime || null,
                startDateStr: startDate || null,
                endDateStr: endDate || null,
            };
            let logs: any = await AuditLogsQuery.GetAuditLogs(requestParams);
            setTotalCount(logs.data.totalCount);
            setAudiLogs(logs.data.item);
        } catch (error) {
            console.log(t('auditLogs.messages.errorOccurred'), error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        setPageNumber(0);
        setRefresh(!refresh);
    };

    const handleClearSearch = () => {
        setSearchPath('');
        setSearchUserName('');
        setPageNumber(0);
        setSearchPath('');
        setSearchResponseTime(0);
        setStartDate(null);
        setEndDate(null);
        setRefresh(!refresh);
    };

    useEffect(() => {
        GetAuditLogs();
    }, [refresh, pageNumber, pageSize]);

    return (
        <SCUIContent>
            <Grid className="audit-logs" container>
                <SCUIBreadCrumb title={t('auditLogs.title')} componentLink={'AuditLogs'} />
                <Box
                    className="audit-logs-search-panel"
                    sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 2 }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid
                            className="audit-logs-search-path"
                            size={{
                                xs: 12,
                                sm: 2,
                            }}
                        >
                            <SCUITextField
                                fullWidth
                                label={t('auditLogs.search.path')}
                                value={searchPath}
                                onChange={(textValue: string) => setSearchPath(textValue)}
                                placeholder={t('auditLogs.search.pathPlaceholder')}
                                stateName="searchPath"
                            />
                        </Grid>
                        <Grid
                            className="audit-logs-search-user-name"
                            size={{
                                xs: 12,
                                sm: 2,
                            }}
                        >
                            <SCUITextField
                                fullWidth
                                label={t('auditLogs.search.userName')}
                                value={searchUserName}
                                onChange={(textValue: string) => setSearchUserName(textValue)}
                                placeholder={t('auditLogs.search.userNamePlaceholder')}
                                stateName="searchUserName"
                            />
                        </Grid>
                        <Grid
                            className="audit-logs-search-response-time"
                            size={{
                                xs: 12,
                                sm: 2,
                            }}
                        >
                            <SCUITextField
                                fullWidth
                                label={t('auditLogs.search.responseTimeMs')}
                                value={searchResponseTime}
                                onChange={(textValue: string) => setSearchResponseTime(textValue)}
                                placeholder={t('auditLogs.search.responseTimeMs')}
                                stateName="responseTimeMs"
                            />
                        </Grid>
                        <Grid
                            className="audit-logs-date-range"
                            size={{
                                xl: 2,
                                lg: 2,
                                md: 12,
                                xs: 12,
                            }}
                        >
                            <SCUIDateRanger size="3.5rem" setDateRange={_handlChangeDate} dateFormat={'DD/MM/YYYY'} />
                        </Grid>
                        <Grid
                            className="audit-logs-search-actions"
                            size={{
                                xs: 12,
                                sm: 4,
                                md: 3,
                            }}
                        >
                            <Box
                                className="audit-logs-search-actions-inner"
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    height: '35px',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <SCUIButton
                                    className="audit-logs-search-button"
                                    type="primary"
                                    sizeType="default"
                                    size="m"
                                    text={t('auditLogs.search.searchButton')}
                                    showStartIcon={true}
                                    startIcon={
                                        <SearchIcon
                                            sx={{ fontSize: '16px', color: '#fff', height: '24px', width: '24px' }}
                                        />
                                    }
                                    onClick={handleSearch}
                                    style={{ flex: 1, height: '35px' }}
                                />
                                <SCUIButton
                                    className="audit-logs-clear-button"
                                    type="secondary"
                                    sizeType="default"
                                    size="m"
                                    text={t('auditLogs.search.clearButton')}
                                    onClick={handleClearSearch}
                                    style={{ flex: 1, height: '35px' }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <SCUILoadingLinear display={isLoading} />
                <Box className="audit-logs-data-grid" sx={{ width: '100%' }}>
                    <SCUICustomDataGrid
                        randomId
                        rows={auditLogs}
                        columns={columns}
                        onPageSizeChange={(i: number) => {
                            setPageSize(i);
                            setPageNumber(0);
                        }}
                        onPageChange={(i: number) => {
                            setPageNumber(i);
                        }}
                        page={pageNumber}
                        pageSize={pageSize}
                        rowCount={totalCount}
                        paginationModeChange
                    />
                </Box>
            </Grid>
        </SCUIContent>
    );
};
